import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContactDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const SalesFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, salesPrefix, setSalesPrefix, salesRequest } = useTaskTablesContext();

  const options = useSubscribe(
    salesRequest.data$,
    (response) => response?.data.items.map((item) => ({ label: item.name ?? `${item.firstName} ${item.lastName}`, value: item })) ?? [],
  );
  const total = useSubscribe(salesRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(salesRequest.pending$);

  const handleChange = useCallbackRef((option: Option<ContactDto> | null) => {
    updateFilter('sales', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: salesPrefix, setFilter: setSalesPrefix, total }),
    [salesPrefix, setSalesPrefix, total],
  );

  return (
    <Filter<ContactDto>
      label={t('common:SALES')}
      options={options}
      selected={filters.sales}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='sales-filter'
    />
  );
};
